import React from 'react'

import Locale from 'data/locale'
import './App.scss'

import Store from 'store'
import History, { useCanUndo } from 'store/history'
import Session from 'store/session'

import EditingContext from 'store/editingContext'
import { ModifierContext } from 'ui/modifiers'
import OverlayRoot, { OverlayContext } from 'ui/utils/OverlayManager'

import EquipmentPane from 'components/equipment/EquipmentPane'
import ImportPane from 'components/import/ImportPane'
import ClassSelect from 'components/paperdoll/ClassSelect'
import Paperdoll from 'components/paperdoll/Paperdoll'
import ProfileList from 'components/paperdoll/ProfileList'
import Paragon from 'components/paragon/Paragon'
import SkillsPane from 'components/skills/SkillsPane'
import Stats from 'components/stats/Stats'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import Tabs from 'ui/tabs'

import {
  BuildInfoLevel,
  getStoredValue,
  HasPTR,
  LocaleNames,
  PTR,
  setStoredValue
} from 'common'
import BuildInfo from 'components/buildinfo/BuildInfo'
import DragLayer from 'components/DragLayer'
import { ErrorPopup } from 'components/import/useLoadProfile'
import HistoryChart from 'components/stats/HistoryChart'
import { exportAllProfiles } from 'logic/import'
import Select from 'ui/select'
import Overlay from 'ui/utils/Overlay'

const _L = Locale('title')

const tabComponents = {
  buildinfo: BuildInfo,
  equipment: EquipmentPane,
  paragon: Paragon,
  skills: SkillsPane,
  import: ImportPane
}

const Header = React.memo(function Header() {
  const [locale, setLocale] = React.useState(() =>
    getStoredValue('locale', 'enUS')
  )
  const onChangeLocale = React.useCallback((value) => {
    setLocale(value.id)
    setStoredValue('locale', value.id)
    setTimeout(() => window.location.reload())
  }, [])

  const stateStore = Store.useStore()
  const historyStore = History.useStore()
  const canUndo = useCanUndo()

  const togglePTR = React.useCallback(() => {
    if (canUndo) {
      const state = stateStore.getState()
      const historyState = historyStore.getState()
      const backup = exportAllProfiles(state, historyState)
      backup.auto = true
      setStoredValue('stateBackup', backup)
    }

    if (PTR) {
      window.location.pathname = window.location.pathname.replace(
        'd3planner-ptr',
        'd3planner'
      )
    } else {
      window.location.pathname = window.location.pathname.replace(
        'd3planner',
        'd3planner-ptr'
      )
    }
  }, [stateStore, historyStore, canUndo])

  return (
    <div className="d3p-Header">
      <div className="d3p-logo" />
      {HasPTR && (
        <div className="d3p-ptr">
          <form>
            <label>
              <input
                type="radio"
                checked={!PTR}
                onChange={PTR ? togglePTR : null}
                readOnly={!PTR}
              />{' '}
              Live
            </label>
            <label>
              <input
                type="radio"
                checked={PTR}
                onChange={PTR ? null : togglePTR}
                readOnly={PTR}
              />{' '}
              PTR
            </label>
          </form>
        </div>
      )}
      <div className="d3p-content">
        <Select
          value={{ id: locale, name: LocaleNames[locale] }}
          onChange={onChangeLocale}
          options={Object.entries(LocaleNames).map(([id, name]) => ({
            id,
            name
          }))}
          maxHeight={350}
        />
      </div>
      {/* <div className="d3p-login">
        <LoginBar />
      </div> */}
    </div>
  )
})

function App({ loading }) {
  const overlays = React.useContext(OverlayContext)
  const [spinner, setSpinner] = EditingContext.useState('spinner', 0)

  const [tab, setTab] = React.useState('equipment')

  const userLevel = Session.useSelector((store) => store.userLevel || 0)
  const hasBuildInfo = Store.useProfile((profile) => !!profile.buildinfo?.text)
  const showBuildInfo = userLevel >= BuildInfoLevel || hasBuildInfo

  const title = Store.useSelector((state) => state.name)
  React.useEffect(() => {
    const _Lt = Locale('title')
    const pageTitle = _Lt`Diablo III Character Planner`
    if (title) {
      document.title = title + ' - ' + pageTitle
    } else {
      document.title = pageTitle
    }
  }, [title])

  React.useEffect(() => {
    if (!loading) return
    let active = true
    const cleanup = () => {
      if (active) {
        setSpinner((spin) => spin - 1)
        active = false
      }
    }
    loading
      .then((error) => {
        if (error && active) {
          overlays.async(<ErrorPopup reason={error} />)
        }
      })
      .finally(cleanup)
    return cleanup
  }, [loading, setSpinner, overlays])

  const tabList = React.useMemo(() => {
    const tabs = [
      { id: 'buildinfo', name: _L`Guide` },
      { id: 'equipment', name: _L`Equipment` },
      { id: 'paragon', name: _L`Paragon/Altar` },
      { id: 'skills', name: _L`Skills/Effects` },
      { id: 'import', name: _L`Import/Save` }
    ]
    if (!showBuildInfo) {
      tabs.splice(0, 1)
    }
    return tabs
  }, [showBuildInfo])

  const editingCallback = React.useCallback(
    ({ type }) => {
      switch (type) {
        case 'buildinfo':
          if (showBuildInfo) {
            setTab('buildinfo')
            return true
          }
          break
        case 'item':
          setTab('equipment')
          break
        case 'skill':
        case 'passive':
          setTab('skills')
          break
        case 'paragon':
          setTab('paragon')
          return true
        case 'cube':
          setTab((prevTab) => (prevTab === 'equipment' ? prevTab : 'skills'))
          break
        // no default
      }
    },
    [showBuildInfo]
  )
  EditingContext.useHandler(editingCallback)

  React.useLayoutEffect(() => {
    if (!showBuildInfo && tab === 'buildinfo') {
      setTab('equipment')
    }
  }, [tab, showBuildInfo])

  const TabComponent = tabList.find((t) => t.id === tab)
    ? tabComponents[tab]
    : null

  return (
    <div className="d3planner-App">
      <Header />
      <div className="d3p-Body">
        <div className="d3p-LeftColumn">
          <ClassSelect />
          <Paperdoll />
          <ProfileList />
        </div>
        <Tabs
          tabs={tabList}
          value={tab}
          onChange={setTab}
          className="d3p-MainColumn">
          {!!TabComponent && <TabComponent />}
        </Tabs>
        <div className="d3p-RightColumn">
          <Stats />
          <HistoryChart />
        </div>
      </div>
      <DragLayer />
      {spinner > 0 && (
        <Overlay>
          <div className="d3p-Spinner" />
        </Overlay>
      )}
    </div>
  )
}

export default function AppContext({
  history,
  session,
  loading,
  editingContext
}) {
  return (
    <React.Fragment>
      <DndProvider backend={HTML5Backend}>
        <Store.Provider store={Store.instance}>
          <History.Provider store={history}>
            <Session.Provider store={session}>
              <EditingContext.Provider context={editingContext}>
                <ModifierContext>
                  <OverlayRoot>
                    <App loading={loading} />
                  </OverlayRoot>
                </ModifierContext>
              </EditingContext.Provider>
            </Session.Provider>
          </History.Provider>
        </Store.Provider>
      </DndProvider>
    </React.Fragment>
  )
}
